import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Skeleton from "react-loading-skeleton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FleetrifyModal } from "../../../Modal/modal";
import { FleetrifyInputField } from "../../../Input/input";
import FleetrifyPasswordChecker from "../../../Password/password-checker";
import FleetrifyPasswordMatch from "../../../Password/password-match";
import {
  useAcceptInvitationMutation,
  useGetSingleInvitedUserQuery,
} from "../../../../redux/services/inviteApi";
import { FLEETRIFYROUTEPATH } from "../../../../routes/routesPath";
import { getApiErrorMessage } from "../../../../utils/apiError";

const AcceptStaffInviteModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userID = searchParams.get("id");
  const viewParam = searchParams.get("view");

  // const {
  //   data: invitedUserData,
  //   isLoading: isLoadingInvitedUser,
  //   isError: isErrorInvitedUser,
  // } = useGetSingleInvitedUserQuery(userID ?? "", {
  //   skip: !userID || viewParam !== "accept",
  // });

  const {
    data: invitedUserData,
    isLoading: isLoadingInvitedUser,
    isError: isErrorInvitedUser,
  } = useGetSingleInvitedUserQuery(userID ?? "", {
    skip: !userID,
  });

  const [acceptInvitation, { isLoading }] = useAcceptInvitationMutation();
  const invitedUser = invitedUserData?.data;

  const isDisabled = !password || !confirmPassword || password !== confirmPassword;

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      toast.error("Please fill in both password fields.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      await acceptInvitation({
        id: String(userID),
        password,
        password_confirmation: confirmPassword,
      }).unwrap();

      toast.success("Invitation accepted successfully!", {
        style: {
          background: "#e0f2fe",
          color: "#1E40AF",
          border: "1px solid #1E40AF",
        },
      });

      navigate(FLEETRIFYROUTEPATH.LOGIN);
      onClose?.();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, "Failed to accept invite"), {
        style: {
          background: "#ffe5e5",
          color: "#ef4444",
          border: "1px solid #ff4d4f",
        },
      });
    }
  };

  if (!isOpen) return null;

  // ✅ Show loader while fetching invited user details
  if (isLoadingInvitedUser) {
    return (
      <FleetrifyModal
        disableClose
        isOpen={isOpen}
        onClose={onClose}
        overlayClassName="z-[9999]"
        enableOutsideClick={false}
        className="max-w-2xl h-fit"
      >
        <div className="flex flex-col justify-center items-center h-[40vh] space-y-4">
          <Skeleton width={200} height={25} />
          <Skeleton width={300} height={20} />
          <Skeleton width={250} height={20} />
        </div>
      </FleetrifyModal>
    );
  }

  // error handling
  if (isErrorInvitedUser) {
    return (
      <FleetrifyModal isOpen={isOpen} onClose={onClose}>
        <div className="p-10 text-center">
          <p className="text-red-600 font-medium">
            Failed to load invited user details.
          </p>
          <button
            onClick={() => navigate(FLEETRIFYROUTEPATH.LOGIN)}
            className="mt-4 px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Go Back
          </button>
        </div>
      </FleetrifyModal>
    );
  }

  return (
    <FleetrifyModal
      disableClose
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-2xl max-h-[85vh] hide-scrollbar"
      childrenClassName="flex flex-col pb-0 overflow-hidden"
      overlayClassName="z-[9999]"
      enableOutsideClick={false}
    >
      <div className="flex flex-col flex-1 min-h-0 px-10">
        {/* header */}
        <div className="space-y-5">
          <h1 className="text-[#00032A] text-[20px] font-bold">
            Dear{" "}
            {!invitedUser?.first_name && !invitedUser?.last_name ? (
              <Skeleton width={120} height={20} />
            ) : (
              <span className="font-extrabold capitalize">
                {invitedUser?.first_name} {invitedUser?.last_name}
              </span>
            )}
          </h1>
          <p className="text-[#00032A]">
            Please set your password to complete your invitation acceptance.
          </p>
        </div>

        {/* scrollable form */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-2 hide-scrollbar space-y-6 py-6">
          <FleetrifyInputField
            label="First Name"
            value={invitedUser?.first_name ?? ""}
            disabled
            onChange={() => { }}
          />

          <FleetrifyInputField
            label="Last Name"
            value={invitedUser?.last_name ?? ""}
            disabled
            onChange={() => { }}
          />

          <FleetrifyInputField
            label="Email Address"
            value={invitedUser?.email ?? ""}
            disabled
            onChange={() => { }}
          />

          <FleetrifyInputField
            type={showPassword ? "text" : "password"}
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="***********************"
            icon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <FleetrifyPasswordChecker password={password} />

          <FleetrifyInputField
            type={showConfirm ? "text" : "password"}
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="***********************"
            icon={
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <FleetrifyPasswordMatch password={password} confirmPassword={confirmPassword} />
        </div>

        {/* footer */}
        <div className="sticky bottom-0 bg-white py-4 mt-6 flex justify-end border-t border-gray-200">
          <button
            onClick={handleSubmit}
            disabled={isDisabled || isLoading}
            className={`px-10 py-2 rounded-md transition-colors ${isDisabled || isLoading
              ? "bg-blue-400 text-white cursor-not-allowed"
              : "bg-[#1E40AF] hover:bg-blue-800 text-white"
              }`}
          >
            {isLoading ? "Processing..." : "Continue"}
          </button>
        </div>
      </div>
    </FleetrifyModal>
  );
};

export default AcceptStaffInviteModal;
